/**
 * Pagamentos — a camada de API do painel para a Fase 5.
 *
 * ---------------------------------------------------------------------------
 * A MÁQUINA MORA NO SHARED; A TELA SÓ PERGUNTA
 * ---------------------------------------------------------------------------
 * Quais ações aparecem em cada transação vem de
 * `transicoesDePagamentoPossiveis(status, 'EMPRESA')` — a MESMA lista fechada
 * que a API valida. A tela nunca decide se capturar/baixar/estornar existe: ela
 * desenha o que a máquina declara, e um 422 de estado defasado invalida a query.
 *
 * ---------------------------------------------------------------------------
 * IDEMPOTÊNCIA NA CRIAÇÃO (invariante nº 9)
 * ---------------------------------------------------------------------------
 * Venda, caução e renovação exigem `Idempotency-Key`. A chave nasce quando o
 * DIALOG abre — não no clique: dois cliques no mesmo formulário são a mesma
 * intenção e devolvem a MESMA transação, nunca duas cobranças.
 *
 * PCI: nenhum campo de número de cartão existe aqui. `tokenDoCartao` é o token
 * da tokenização no provedor; bandeira e últimos quatro são só os exibíveis.
 */

import {
  METODOS_DE_PAGAMENTO,
  PROVEDORES_DE_PAGAMENTO,
  STATUS_DE_TRANSACAO,
  STATUS_TERMINAIS_DE_TRANSACAO,
  TIPOS_DE_TRANSACAO,
  precisaRenovarPreAutorizacao,
  transicoesDePagamentoPossiveis,
  type MetodoDePagamento,
  type StatusDeTransacao,
} from '@locaveri/shared'
import {
  useMutation,
  type UseMutationResult,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query'
import { z } from 'zod'

import { paginaDe } from '@/features/precos/api'
import { apiGet, apiPost } from '@/lib/api'
import { ROTAS_DE_PAGAMENTOS } from '@/lib/endpoints'
import { montarQuery } from '@/lib/query-string'

// ---------------------------------------------------------------------------
// Vocabulário local
// ---------------------------------------------------------------------------

/**
 * As finalidades, na MESMA lista do enum da API (`relatorios.dto.ts`). Locais
 * porque `@locaveri/shared` não expõe a união — se ela ganhar nome lá, esta
 * some e o import muda de lugar.
 */
export const FINALIDADES_DE_TRANSACAO = [
  'VENDA_DA_RESERVA',
  'CAUCAO',
  'RENOVACAO_DE_CAUCAO',
  'FATURA',
  'MULTA',
] as const

export type FinalidadeDeTransacao = (typeof FINALIDADES_DE_TRANSACAO)[number]

export const ROTULO_DA_FINALIDADE: Readonly<Record<FinalidadeDeTransacao, string>> = {
  VENDA_DA_RESERVA: 'Venda da reserva',
  CAUCAO: 'Caução',
  RENOVACAO_DE_CAUCAO: 'Renovação de caução',
  FATURA: 'Fatura',
  MULTA: 'Multa de trânsito',
}

// ---------------------------------------------------------------------------
// Modelo de leitura (espelha `pagamentos.dto.ts` da API)
// ---------------------------------------------------------------------------

export const transacaoSchema = z.object({
  id: z.uuid(),
  reservationId: z.uuid().nullable(),
  invoiceId: z.uuid().nullable(),

  finalidade: z.enum(FINALIDADES_DE_TRANSACAO),
  tipo: z.enum(TIPOS_DE_TRANSACAO),
  metodo: z.enum(METODOS_DE_PAGAMENTO),
  status: z.enum(STATUS_DE_TRANSACAO),
  provedor: z.enum(PROVEDORES_DE_PAGAMENTO),

  referencia: z.string(),
  transactionIdExterno: z.string().nullable(),

  /** Centavos como string decimal (o wire de `Money`). */
  valor: z.string(),
  valorCapturado: z.string(),
  valorEstornado: z.string(),
  parcelas: z.number().int(),

  bandeira: z.string().nullable(),
  ultimosQuatro: z.string().nullable(),
  /** PIX aguardando pagamento: o payload copia-e-cola do QR. */
  qrCode: z.string().nullable(),

  autorizacaoExpiraEm: z.string().nullable(),
  codigoDoProvedor: z.string().nullable(),
  mensagemDoProvedor: z.string().nullable(),
  motivo: z.string().nullable(),

  criadoEm: z.string(),
  atualizadoEm: z.string(),
})

export type Transacao = z.infer<typeof transacaoSchema>

const listaDeTransacoesSchema = z.object({ itens: z.array(transacaoSchema) })
const paginaDeTransacoesSchema = paginaDe(transacaoSchema)

export type PaginaDeTransacoes = z.infer<typeof paginaDeTransacoesSchema>

export const caucaoParaRenovarSchema = z.object({
  transacaoId: z.uuid(),
  reservationId: z.uuid().nullable(),
  /** Centavos como string. */
  valor: z.string(),
  autorizacaoExpiraEm: z.string(),
  /** `true` = o bloqueio JÁ venceu no emissor — risco descoberto. */
  vencida: z.boolean(),
})

export type CaucaoParaRenovar = z.infer<typeof caucaoParaRenovarSchema>

const listaDeCaucoesSchema = z.object({ itens: z.array(caucaoParaRenovarSchema) })

export const relatorioDeReconciliacaoSchema = z.object({
  /** Transações há mais de 10 min sem desfecho — o que a reconciliação varre. */
  naoTerminais: z.array(
    z.object({
      id: z.uuid(),
      reservationId: z.uuid().nullable(),
      status: z.enum(STATUS_DE_TRANSACAO),
      finalidade: z.enum(FINALIDADES_DE_TRANSACAO),
      metodo: z.enum(METODOS_DE_PAGAMENTO),
      transactionIdExterno: z.string().nullable(),
      criadoEm: z.string(),
    }),
  ),
  /** Eventos do tenant com falha de processamento (a face visível da DLQ). */
  eventosComFalha: z.array(
    z.object({
      id: z.uuid(),
      transactionId: z.uuid().nullable(),
      transactionIdExterno: z.string(),
      tipoCru: z.string(),
      tentativas: z.number().int(),
      erro: z.string().nullable(),
      recebidoEm: z.string(),
      processadoEm: z.string().nullable(),
    }),
  ),
  /** Contagem GLOBAL de eventos órfãos (sem tenant) — só o número. */
  orfaosNoProvedor: z.number().int().nonnegative(),
})

export type RelatorioDeReconciliacao = z.infer<typeof relatorioDeReconciliacaoSchema>

// ---------------------------------------------------------------------------
// O que cada status permite (a leitura da máquina, nunca `if` de tela)
// ---------------------------------------------------------------------------

function acoesDaEmpresa(status: StatusDeTransacao): ReadonlySet<string> {
  return new Set(transicoesDePagamentoPossiveis(status, 'EMPRESA').map((item) => item.acao))
}

export function podeCapturar(transacao: Transacao): boolean {
  return acoesDaEmpresa(transacao.status).has('CAPTURAR')
}

export function podeBaixar(transacao: Transacao): boolean {
  return acoesDaEmpresa(transacao.status).has('BAIXAR')
}

export function podeEstornar(transacao: Transacao): boolean {
  const acoes = acoesDaEmpresa(transacao.status)
  return acoes.has('ESTORNAR') || acoes.has('ESTORNAR_PARTE')
}

/** Renovar é criar uma pré-autorização nova encadeada — só caução AUTORIZADA. */
export function podeRenovar(transacao: Transacao): boolean {
  return (
    transacao.status === 'AUTORIZADA' &&
    (transacao.finalidade === 'CAUCAO' || transacao.finalidade === 'RENOVACAO_DE_CAUCAO')
  )
}

/** Sincronizar só faz sentido enquanto o provedor ainda pode mudar o estado. */
export function podeSincronizar(transacao: Transacao): boolean {
  return !STATUS_TERMINAIS_DE_TRANSACAO.includes(transacao.status)
}

/**
 * A caução AUTORIZADA entrou na janela de renovação (5 dias antes do
 * vencimento no emissor) — o alerta do Gap 6, com a conta do shared.
 */
export function caucaoPrecisaRenovar(transacao: Transacao): boolean {
  if (transacao.status !== 'AUTORIZADA' || transacao.autorizacaoExpiraEm === null) {
    return false
  }
  const expiraEm = new Date(transacao.autorizacaoExpiraEm)
  return !Number.isNaN(expiraEm.getTime()) && precisaRenovarPreAutorizacao(expiraEm, new Date())
}

// ---------------------------------------------------------------------------
// Filtros e chaves
// ---------------------------------------------------------------------------

export interface FiltrosDeTransacoes {
  readonly pagina: number
  readonly tamanho: number
  readonly status: StatusDeTransacao | ''
  readonly finalidade: FinalidadeDeTransacao | ''
  readonly metodo: MetodoDePagamento | ''
}

export const CHAVE_DE_PAGAMENTOS = 'pagamentos'

export const chavesDePagamentos = {
  daReserva: (reservaId: string) => [CHAVE_DE_PAGAMENTOS, 'reserva', reservaId] as const,
  lista: (filtros: FiltrosDeTransacoes) => [CHAVE_DE_PAGAMENTOS, 'lista', filtros] as const,
  renovacoes: () => [CHAVE_DE_PAGAMENTOS, 'renovacoes'] as const,
  reconciliacao: () => [CHAVE_DE_PAGAMENTOS, 'reconciliacao'] as const,
}

/**
 * Toda escrita invalida o módulo inteiro E as reservas: a ponte por eventos
 * muda o status da reserva quando a venda confirma — o detalhe aberto precisa
 * acompanhar sem F5.
 */
function invalidarPagamentos(queryClient: ReturnType<typeof useQueryClient>): void {
  void queryClient.invalidateQueries({ queryKey: [CHAVE_DE_PAGAMENTOS] })
  void queryClient.invalidateQueries({ queryKey: ['reservas'] })
}

// ---------------------------------------------------------------------------
// Leitura
// ---------------------------------------------------------------------------

export function useTransacoesDaReserva(
  reservaId: string | null,
): UseQueryResult<{ itens: Transacao[] }> {
  return useQuery({
    queryKey: chavesDePagamentos.daReserva(reservaId ?? 'nenhuma'),
    queryFn: ({ signal }) =>
      apiGet(ROTAS_DE_PAGAMENTOS.daReserva(reservaId ?? ''), listaDeTransacoesSchema, { signal }),
    enabled: reservaId !== null,
  })
}

export function useTransacoes(filtros: FiltrosDeTransacoes): UseQueryResult<PaginaDeTransacoes> {
  return useQuery({
    queryKey: chavesDePagamentos.lista(filtros),
    queryFn: ({ signal }) => {
      const query = montarQuery({
        pagina: filtros.pagina,
        tamanho: filtros.tamanho,
        status: filtros.status,
        finalidade: filtros.finalidade,
        metodo: filtros.metodo,
      })
      return apiGet(`${ROTAS_DE_PAGAMENTOS.lista}${query}`, paginaDeTransacoesSchema, { signal })
    },
    // Mantém a página anterior enquanto a nova carrega: sem isso a tabela
    // pisca em branco a cada mudança de filtro.
    placeholderData: (anterior) => anterior,
  })
}

export function useCaucoesParaRenovar(): UseQueryResult<{ itens: CaucaoParaRenovar[] }> {
  return useQuery({
    queryKey: chavesDePagamentos.renovacoes(),
    queryFn: ({ signal }) =>
      apiGet(ROTAS_DE_PAGAMENTOS.renovacoesPendentes, listaDeCaucoesSchema, { signal }),
  })
}

export function useRelatorioDeReconciliacao(): UseQueryResult<RelatorioDeReconciliacao> {
  return useQuery({
    queryKey: chavesDePagamentos.reconciliacao(),
    queryFn: ({ signal }) =>
      apiGet(ROTAS_DE_PAGAMENTOS.reconciliacao, relatorioDeReconciliacaoSchema, { signal }),
  })
}

// ---------------------------------------------------------------------------
// Escrita
// ---------------------------------------------------------------------------

/**
 * O corpo de criação de cobrança (o shape de `criarCobrancaSchema` da API).
 * O VALOR nunca viaja: a venda usa o total congelado e a caução usa a caução
 * congelada — o painel não digita dinheiro em cobrança.
 */
export interface CorpoDeCobranca {
  readonly metodo: MetodoDePagamento
  readonly parcelas?: number
  readonly tokenDoCartao?: string
  readonly bandeira?: string
  readonly ultimosQuatro?: string
  readonly nomeDoPortador?: string
}

export interface CriacaoDeCobranca {
  readonly corpo: CorpoDeCobranca
  /** Nasce quando o dialog abre — ver o cabeçalho do arquivo. */
  readonly chaveDeIdempotencia: string
}

export function useCriarVenda(
  reservaId: string,
): UseMutationResult<Transacao, Error, CriacaoDeCobranca> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ corpo, chaveDeIdempotencia }) =>
      apiPost(ROTAS_DE_PAGAMENTOS.venda(reservaId), transacaoSchema, corpo, {
        idempotencyKey: chaveDeIdempotencia,
      }),
    onSuccess: () => {
      invalidarPagamentos(queryClient)
    },
  })
}

export function useCriarCaucao(
  reservaId: string,
): UseMutationResult<Transacao, Error, CriacaoDeCobranca> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ corpo, chaveDeIdempotencia }) =>
      apiPost(ROTAS_DE_PAGAMENTOS.caucao(reservaId), transacaoSchema, corpo, {
        idempotencyKey: chaveDeIdempotencia,
      }),
    onSuccess: () => {
      invalidarPagamentos(queryClient)
    },
  })
}

export function useCapturar(): UseMutationResult<
  Transacao,
  Error,
  { readonly id: string; readonly valor: string | null }
> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, valor }) =>
      apiPost(
        ROTAS_DE_PAGAMENTOS.capturar(id),
        transacaoSchema,
        // Sem valor no corpo = captura total (o contrato da API).
        valor === null ? {} : { valor },
      ),
    onSuccess: () => {
      invalidarPagamentos(queryClient)
    },
  })
}

export function useBaixarTransacao(): UseMutationResult<Transacao, Error, { readonly id: string }> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id }) => apiPost(ROTAS_DE_PAGAMENTOS.baixar(id), transacaoSchema, {}),
    onSuccess: () => {
      invalidarPagamentos(queryClient)
    },
  })
}

export function useEstornarTransacao(): UseMutationResult<
  Transacao,
  Error,
  { readonly id: string; readonly motivo: string; readonly valor: string | null }
> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, motivo, valor }) =>
      apiPost(ROTAS_DE_PAGAMENTOS.estornar(id), transacaoSchema, {
        motivo,
        ...(valor === null ? {} : { valor }),
      }),
    onSuccess: () => {
      invalidarPagamentos(queryClient)
    },
  })
}

export function useRenovarCaucao(): UseMutationResult<
  Transacao,
  Error,
  { readonly id: string } & CriacaoDeCobranca
> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, corpo, chaveDeIdempotencia }) =>
      apiPost(ROTAS_DE_PAGAMENTOS.renovar(id), transacaoSchema, corpo, {
        idempotencyKey: chaveDeIdempotencia,
      }),
    onSuccess: () => {
      invalidarPagamentos(queryClient)
    },
  })
}

export function useSincronizarTransacao(): UseMutationResult<
  Transacao,
  Error,
  { readonly id: string }
> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id }) => apiPost(ROTAS_DE_PAGAMENTOS.sincronizar(id), transacaoSchema, {}),
    onSuccess: () => {
      invalidarPagamentos(queryClient)
    },
  })
}
